import { ChangeEvent, Fragment } from 'react';
import { useAppDispatch } from '../../../../app/hooks';
import { CHARGEBEE_URL } from '../../../../services/BackendService';
import { PlanId, User } from '../../../../services/UsersService';
import UserUpdateButton from '../../UserUpdateButton';
import {
  setHasUnlimRenders,
  setBetaTester,
  setUnlimRendersUntil,
  setRecachingDisabled,
  setManualReactivate,
} from '../UserSlice';
import { useAddAchPaymentMutation } from '../../UsersApiSlice';
import { RequireTier } from '../../../auth/RequireTier';

interface UserBillingTabProps {
  readonly user: User;
}

const UserBillingTab = (props: UserBillingTabProps) => {
  const dispatch = useAppDispatch();
  const [addAchPayment, { error: addAchError, status: addAchStatus }] = useAddAchPaymentMutation();
  let addAchErrorText = (addAchError as any)?.data.message;
  let statusText = addAchStatus === 'uninitialized' ? '' : addAchStatus === 'fulfilled' ? 'ok' : addAchStatus;

  const { user } = props;

  const findInChargebee = () => {
    window.open(`${CHARGEBEE_URL}/customers?view_code=all&Customers.search=${user?.chargebeeCustomerId}`, '_blank');
  };

  const changeHasUnlimRenders = (event: ChangeEvent) => {
    const checked = !user?.hasUnlimRenders;
    dispatch(setHasUnlimRenders(checked));
  };

  const changeBetaTester = (event: ChangeEvent) => {
    const checked = !user?.betaTester;
    dispatch(setBetaTester(checked));
  };

  const changeUnlimRendersUntil = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setUnlimRendersUntil(new Date(event.target.value)));
  };

  const changeRecachingDisabled = (event: ChangeEvent) => {
    const checked = !user.recachingDisabled;
    dispatch(setRecachingDisabled(checked));
  };

  const changeManualReactivate = (event: ChangeEvent) => {
    const checked = !user.settings.manualReactivate;
    dispatch(setManualReactivate(checked));
  };

  const getDateString = (d: string | null): string => {
    let date = d ? new Date(d) : new Date();

    const offsetMs = date.getTimezoneOffset() * 60 * 1000;
    const msLocal = date.getTime() - offsetMs;
    const dateLocal = new Date(msLocal);
    const iso = dateLocal.toISOString();
    const isoLocal = iso.slice(0, 19);
    return isoLocal;
  };

  const addAchPaymentClick = () => {
    addAchPayment(user.id);
  };

  return (
    <Fragment>
      <div className="row align-items-center">
        <div className="col">
          <div className="form-check form-switch">
            <label htmlFor="isMetered" className="form-label">
              Is Metered
            </label>
            <input
              type="checkbox"
              className="form-check-input"
              id="isMetered"
              checked={user.isMetered}
              disabled
              readOnly
            />
          </div>
        </div>
        {user.isMetered && (
          <div className="col">
            <select
              className="form-select mb-3"
              data-choices
              defaultValue={user.plan.chargeBeePlanId ? user.plan.chargeBeePlanId : ('basic-001' as PlanId)}
              disabled
            >
              <option>{'basic-001' as PlanId}</option>
              <option>{'pro-001' as PlanId}</option>
              <option>{'startup-001' as PlanId}</option>
              <option>{'business-001' as PlanId}</option>
              <option>{'enterprise-001' as PlanId}</option>
              <option>{'startup-2022' as PlanId}</option>
              <option>{'scaleup-2022' as PlanId}</option>
              <option>{'enterprise-2022' as PlanId}</option>
              <option>{'plan-2024-050' as PlanId}</option>
              <option>{'plan-2024-100' as PlanId}</option>
              <option>{'plan-2024-200' as PlanId}</option>
              <option>{'plan-2024-300' as PlanId}</option>
              <option>{'custom-enterprise-001' as PlanId}</option>
              <option>{'custom-enterprise-yearly-001' as PlanId}</option>
              <option>{'starter-2025' as PlanId}</option>
              <option>{'growth-2025' as PlanId}</option>
              <option>{'pro-2025' as PlanId}</option>
              <option>{'enterprise-plus-2025' as PlanId}</option>
              <option>{'poc-2025' as PlanId}</option>
            </select>
          </div>
        )}
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <label htmlFor="chargebeeCustomerId" className="form-label">
              Chargebee customer id
            </label>
            <input
              className="form-control"
              id="chargebeeCustomerId"
              defaultValue={user.chargebeeCustomerId}
              disabled
              readOnly
            />
          </div>
        </div>
        <div className="col text-center">
          <button onClick={findInChargebee} className="btn btn-primary">
            Find in chargebee
          </button>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col my-4">
          <RequireTier requiredTier="superadmin" policy="disable">
            <button className="btn btn-primary" onClick={addAchPaymentClick}>
              Add ACH payment
            </button>
          </RequireTier>
        </div>
        <div className="col">{statusText}</div>
        <div className="col fw-bold">{addAchErrorText && `Error: ${addAchErrorText}`}</div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-check form-switch">
            <label htmlFor="isCustomPricing" className="form-label">
              Is custom pricing
            </label>
            <input
              type="checkbox"
              className="form-check-input"
              id="isCustomPricing"
              checked={user.isCustomPricing}
              disabled
              readOnly
            />
          </div>
        </div>
        {user.isCustomPricing && (
          <div className="col">
            <div className="form-group">
              <label htmlFor="customCachedPagesLimit" className="form-label">
                custom cached pages limit
              </label>
              <input
                type="number"
                className="form-control"
                id="customCachedPagesLimit"
                defaultValue={user.customCachedPagesLimit || 0}
                disabled
                readOnly
              />
            </div>
            <div>
              <label htmlFor="customPrice" className="form-label">
                custom price
              </label>
              <input
                type="number"
                className="form-control"
                id="customPrice"
                defaultValue={user.customPrice || 0}
                disabled
                readOnly
              />
            </div>
          </div>
        )}
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-check form-switch">
            <label htmlFor="cancelled" className="form-label">
              cancelled
            </label>
            <input
              type="checkbox"
              className="form-check-input"
              id="cancelled"
              checked={user.cancelled}
              disabled
              readOnly
            />
          </div>
        </div>
        {user.cancelled && (
          <div className="col">
            <div className="form-group">
              <label htmlFor="cancelledAt" className="form-label">
                cancelled at (local)
              </label>
              <input
                className="form-control"
                type="datetime-local"
                id="cancelled at"
                defaultValue={getDateString(user.cancelledAt)}
                disabled
                readOnly
              />
            </div>
          </div>
        )}
      </div>

      <div className="row align-items-center">
        <div className="form-group">
          <div className="form-check form-switch">
            <label htmlFor="recachingDisabled" className="form-label">
              reacaching disabled
            </label>
            <RequireTier requiredTier="admin" policy="disable">
              <input
                type="checkbox"
                className="form-check-input"
                id="recachingDisabled"
                checked={user.recachingDisabled}
                onChange={changeRecachingDisabled}
              />
            </RequireTier>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <label htmlFor="lastCharged" className="form-label">
              last charged
            </label>
            <input className="form-control" id="lastCharged" defaultValue={user.lastCharged || 0} disabled readOnly />
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <label htmlFor="lastCharged" className="form-label">
              End of future renewal
            </label>
            <input
              className="form-control"
              id="lastCharged"
              defaultValue={user.endOfFutureRenewal || '-'}
              disabled
              readOnly
            />
          </div>
        </div>
      </div>

      <RequireTier requiredTier="superadmin" policy="disable">
        <fieldset className="border-0 p-0 m-0">
          <div className="row align-items-center">
            <div className="col">
              <div className="form-check form-switch">
                <label htmlFor="hasUnlimRenders" className="form-label">
                  Has unlim renders
                </label>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="hasUnlimRenders"
                  checked={user.hasUnlimRenders}
                  onChange={changeHasUnlimRenders}
                />
              </div>
            </div>
            {user.hasUnlimRenders && (
              <div className="col">
                <div className="form-group">
                  <label htmlFor="unlimRendersUntil" className="form-label">
                    Unlim renders until (Local)
                  </label>
                  <input
                    className="form-control"
                    type="datetime-local"
                    id="unlimRendersUntil"
                    onChange={changeUnlimRendersUntil}
                    value={getDateString(user.unlimRendersUntil)}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="row align-items-center">
            <div className="col">
              <div className="form-check form-switch">
                <label htmlFor="betaTester" className="form-label">
                  Beta tester
                </label>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="betaTester"
                  checked={user.betaTester}
                  onChange={changeBetaTester}
                />
              </div>
            </div>
          </div>
        </fieldset>
      </RequireTier>
      <div className="row align-items-center">
        <div className="col">
          <div className="form-check form-switch">
            <label htmlFor="manualReactivate" className="form-label">
              Manual reactivate
            </label>
            <RequireTier requiredTier="admin" policy="disable">
              <input
                type="checkbox"
                className="form-check-input"
                id="manualReactivate"
                checked={user.settings.manualReactivate}
                onChange={changeManualReactivate}
              />
            </RequireTier>
          </div>
        </div>
      </div>
      <UserUpdateButton />
    </Fragment>
  );
};

export default UserBillingTab;
